/**
 * DocumentEditor
 * Linkes Dokumenten-Panel im din-rp Stil (light theme, full-height, slide-in).
 *
 * Features:
 * - TipTap-basierter Editor mit vollständiger Formatierungsleiste
 * - Annotation-Links als interaktive Chips einfügen
 * - Scroll-basierte Kameranavigation: beim Scrollen wird die
 *   verknüpfte Annotation im 3D-Viewer angesteuert
 * - Auto-Save mit 500ms Debounce
 */

import React, { useCallback, useRef, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useEditor, EditorContent } from '@tiptap/react'
import { useAnnotationStore, selectActiveDocument } from '../../store/annotations'
import { EMPTY_TIPTAP_DOC, TipTapDocument } from '../../types/annotations'
import { buildEditorExtensions, SlashCommandItem, getSlashQuery } from '../../lib/tiptap'
import { EditorToolbar, ExportFormat } from './EditorToolbar'
import { exportReport } from '../../lib/exportReport'
import { useScrollNavigator, CapturedViewerState } from './ScrollNavigator'
import { useCameraNavigation, captureViewSnapshot, captureCurrentCamera, captureCurrentClipBox, applyViewSnapshot } from '../../hooks/useCameraNavigation'
import { ViewSnapshot } from '../../types/annotations'
import { SnapshotTrail } from './SnapshotTrail'

interface DocumentEditorProps {
  viewerRef: React.RefObject<any>
  currentCloudId?: string | null
  /** When true, renders inside the ContentPanel (no body class toggle, no floating toolbar portal) */
  inline?: boolean
}

/* ── Tiny save-status indicator ─────────────────────────────────────────────── */

const SAVE_LABELS: Record<string, string> = {
  pending: 'Unsaved changes',
  saving: 'Saving …',
  saved: 'Saved',
  error: 'Save failed',
}

function SaveIndicator() {
  const status = useAnnotationStore(s => s.docSaveStatus)
  if (status === 'idle') return null
  return (
    <span className={`doc-save-indicator doc-save-indicator--${status}`}>
      {SAVE_LABELS[status]}
    </span>
  )
}

export function DocumentEditor({ viewerRef, currentCloudId, inline = false }: DocumentEditorProps) {
  const doc = useAnnotationStore(selectActiveDocument)
  const updateDocument = useAnnotationStore(s => s.updateDocument)
  const closeEditor = useAnnotationStore(s => s.closeEditor)
  const openDocumentViewer = useAnnotationStore(s => s.openDocumentViewer)
  const mode = useAnnotationStore(s => s.editor.mode)

  const { flyToCamera } = useCameraNavigation({ viewerRef })
  const getAnnotation = useAnnotationStore(s => s.getAnnotation)
  const setSelectedAnnotation = useAnnotationStore(s => s.setSelectedAnnotation)

  const [title, setTitle] = useState('')
  const [isScrollNavEnabled, setIsScrollNavEnabled] = useState(true)
  const [showHeaderExport, setShowHeaderExport] = useState(false)

  // Callback-Ref → State: garantiert Re-Render wenn das DOM-Element bereit ist
  const [scrollRootEl, setScrollRootEl] = useState<HTMLDivElement | null>(null)
  const scrollRootRef = useCallback((node: HTMLDivElement | null) => {
    setScrollRootEl(node)
    scrollRootElRef.current = node   // Ref für capture-phase mousemove-Listener
  }, [])

  // ── Sync when document changes ────────────────────────────────────────────

  useEffect(() => {
    if (!doc) return
    setTitle(doc.title)
  }, [doc?.id])

  // ── TipTap Editor ──────────────────────────────────────────────────────────

  const handleAnnotationClick = useCallback(
    (annotationId: string) => {
      const annotation = getAnnotation(annotationId)
      if (annotation) {
        flyToCamera(annotation.camera)
        if (annotation.showPopup !== false) {
          setSelectedAnnotation(annotationId)
        }
      }
    },
    [getAnnotation, flyToCamera, setSelectedAnnotation]
  )

  const handleSnapshotClick = useCallback(
    (snapshotJson: string) => {
      const viewer = viewerRef.current
      if (!viewer) return
      try {
        const snap: ViewSnapshot = JSON.parse(snapshotJson)
        applyViewSnapshot(viewer, snap, flyToCamera)
      } catch { /* ungültiger JSON */ }
    },
    [viewerRef, flyToCamera]
  )

  const handlePaperlessDocClick = useCallback(
    (docId: number, title: string) => {
      // Globalen Dokument-Viewer öffnen
      if ((window as any).showDocViewer) {
        ;(window as any).showDocViewer({
          id: docId,
          title,
          fileName: '',
          tags: [],
          created: null,
          pageCount: null,
          docTypeName: null,
        })
      } else {
        import('../../lib/paperless/api').then(({ getDocumentPreviewUrl }) => {
          window.open(getDocumentPreviewUrl(docId), '_blank')
        })
      }
    },
    []
  )

  const handleAuditClick = useCallback(
    (auditId: string) => {
      const audit = ((window as any)._auditObjects || []).find((a: any) => a.id === auditId)
      if (audit && (window as any).navigateToAuditClip) {
        ;(window as any).navigateToAuditClip(audit.clipSnapshot, auditId)
      }
    },
    []
  )

  // ── Slash Commands ──────────────────────────────────────────────────────────
  // Ref-Pattern: die Action-Callbacks greifen auf editorRef.current zu,
  // damit kein zirkulärer Abhängigkeitskreis mit useEditor entsteht.
  const editorRef = useRef<any>(null)

  const slashCommands: SlashCommandItem[] = React.useMemo(() => [
    {
      label: 'Ansicht',
      aliases: ['scene', 'ansicht'],
      description: 'Aktuelle 3D-Ansicht einfügen',
      icon: '📷',
      action: () => {
        const viewer = viewerRef.current
        const ed = editorRef.current
        if (!viewer || !ed) return
        const label = `Ansicht ${new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`
        const snap = captureViewSnapshot(viewer, label)
        ed.chain().focus().insertViewSnapshot({ snapshot: JSON.stringify(snap), label }).run()
      },
    },
    {
      label: 'Audit',
      aliases: ['audit'],
      description: 'Audit einfügen',
      icon: '🔍',
      action: () => {
        const btn = document.querySelector('[title="Audit einfügen"]') as HTMLElement
        if (btn) btn.click()
      },
    },
    {
      label: 'Dokument',
      aliases: ['doc', 'dokument', 'paperless'],
      description: 'Paperless-Dokument einfügen',
      icon: '📄',
      action: () => {
        const btn = document.querySelector('[title="Paperless Dokument einfügen"]') as HTMLElement
        if (btn) btn.click()
      },
    },
    {
      label: 'Notiz',
      aliases: ['notiz', 'annotation'],
      description: 'Annotation verlinken',
      icon: '📌',
      action: () => {
        const btn = document.querySelector('[title="Annotation verlinken"]') as HTMLElement
        if (btn) btn.click()
      },
    },
  ], [viewerRef])

  const extensions = buildEditorExtensions({
    placeholder: 'Dokument beginnen …',
    onAnnotationClick: handleAnnotationClick,
    onSnapshotClick: handleSnapshotClick,
    onPaperlessDocClick: handlePaperlessDocClick,
    onAuditClick: handleAuditClick,
    slashCommands,
  })

  const editor = useEditor({
    extensions,
    content: doc?.content ?? EMPTY_TIPTAP_DOC,
    editable: mode === 'edit',
    onUpdate: ({ editor }) => {
      if (!doc) return
      const json = editor.getJSON() as TipTapDocument
      updateDocument(doc.id, { content: json })
    },
  })

  // Capture-phase click handler on document — intercepts anchor & external
  // links inside the editor before the browser's default navigation fires.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      const a = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null
      if (!a) return
      // Only handle links inside our editor
      const root = scrollRootElRef.current
      if (!root || !root.contains(a)) return
      const href = a.getAttribute('href') ?? ''
      if (href.startsWith('#')) {
        e.preventDefault()
        e.stopImmediatePropagation()
        const slug = href.slice(1)
        const target = root.querySelector(`[id="${CSS.escape(slug)}"]`) as HTMLElement | null
        if (target) {
          const rootRect = root.getBoundingClientRect()
          const targetRect = target.getBoundingClientRect()
          root.scrollTo({ top: root.scrollTop + targetRect.top - rootRect.top - 24, behavior: 'smooth' })
        }
      } else if (href.startsWith('http')) {
        e.preventDefault()
        e.stopImmediatePropagation()
        window.open(href, '_blank', 'noopener,noreferrer')
      }
    }
    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [])

  // editorRef aktuell halten für Slash-Command-Callbacks
  editorRef.current = editor

  // Sync content wenn sich das Dokument ändert
  useEffect(() => {
    if (!editor || !doc) return
    const current = JSON.stringify(editor.getJSON())
    const incoming = JSON.stringify(doc.content)
    if (current !== incoming) {
      editor.commands.setContent(doc.content ?? EMPTY_TIPTAP_DOC, { emitUpdate: false })
    }
  }, [doc?.id, editor])

  // ── Sync editable state bei Modus-Wechsel ──────────────────────────────────
  useEffect(() => {
    if (!editor) return
    const shouldEdit = mode === 'edit'
    if (editor.isEditable !== shouldEdit) {
      editor.setEditable(shouldEdit)
    }
  }, [editor, mode])

  // ── Scroll Navigation ──────────────────────────────────────────────────────

  const captureStateForReturn = useCallback((): CapturedViewerState | null => {
    const viewer = viewerRef.current
    if (!viewer) return null
    return {
      camera: captureCurrentCamera(viewer),
      clipBox: captureCurrentClipBox(),
    }
  }, [viewerRef])

  const { activeAnnotationId } = useScrollNavigator({
    editor,
    enabled: isScrollNavEnabled && mode !== 'edit',
    rootEl: scrollRootEl,
    captureCurrentState: captureStateForReturn,
    onReturnToPrevious: (state) => {
      // Rückkehr zur vorherigen Ansicht → Popup schließen
      setSelectedAnnotation(null)
      flyToCamera(state.camera, 500)
      if (state.clipBox) {
        const clipVolume = (window as any).currentClipVolume
        if (clipVolume) {
          const { position, scale, rotation } = state.clipBox
          clipVolume.position.set(position[0], position[1], position[2])
          clipVolume.scale.set(scale[0], scale[1], scale[2])
          clipVolume.rotation.set(rotation[0], rotation[1], rotation[2])
          clipVolume.update()
        }
      }
    },
    onAnnotationLeave: (_annotationId) => {
      // Annotation scrollt aus dem Viewport → Popup schließen
      setSelectedAnnotation(null)
    },
    onAnnotationVisible: ({ annotationId }) => {
      const annotation = getAnnotation(annotationId)
      if (annotation) {
        flyToCamera(annotation.camera, 600)
        if (annotation.showPopup !== false) {
          setSelectedAnnotation(annotationId)
        }
        // ClipBox auf Bounding Box um die Annotation-Position setzen
        const clipVolume = (window as any).currentClipVolume
        if (clipVolume && annotation.position) {
          const p = annotation.position
          const c = annotation.camera.position
          const t = annotation.camera.target
          // Box-Größe aus Kamera-Abstand ableiten (ca. 60% des Abstands Kamera→Target)
          const dist = Math.sqrt((c.x-t.x)**2 + (c.y-t.y)**2 + (c.z-t.z)**2)
          const boxSize = Math.max(1, dist * 0.6)
          clipVolume.position.set(p.x, p.y, p.z)
          clipVolume.scale.set(boxSize, boxSize, boxSize)
          clipVolume.rotation.set(0, 0, 0)
          clipVolume.updateMatrixWorld(true)
          clipVolume.update()
        }
      }
    },
    onSnapshotVisible: (snapshotJson) => {
      // ViewSnapshot hat keine Annotation → Popup schließen
      setSelectedAnnotation(null)
      const viewer = viewerRef.current
      if (!viewer) return
      try {
        const snap: ViewSnapshot = JSON.parse(snapshotJson)
        applyViewSnapshot(viewer, snap, flyToCamera)
      } catch { /* ungültiger JSON */ }
    },
    onAuditVisible: (auditId) => {
      // Audit in den Viewport gescrollt → zur ClipBox-Position navigieren
      setSelectedAnnotation(null)
      handleAuditClick(auditId)
    },
  })

  // ── View Snapshot einfügen ────────────────────────────────────────────────

  const handleInsertViewSnapshot = useCallback(() => {
    const viewer = viewerRef.current
    if (!viewer || !editor) return
    const label = `Ansicht ${new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`
    const snap = captureViewSnapshot(viewer, label)
    editor.chain().focus().insertViewSnapshot({
      snapshot: JSON.stringify(snap),
      label,
    }).run()
  }, [viewerRef, editor])

  // ── Export ────────────────────────────────────────────────────────────────

  const handleExport = useCallback((format: ExportFormat) => {
    if (!editor || !doc) return
    exportReport(editor, doc.title || 'Report', format)
  }, [editor, doc])

  // ── Title save ────────────────────────────────────────────────────────────

  function handleTitleBlur() {
    if (!doc || !title.trim()) return
    updateDocument(doc.id, { title: title.trim() })
  }

  // ── Pointer-Event-Isolation: verhindert, dass Wheel/Mouse-Events
  //    durch das Panel an Potrees Canvas durchgereicht werden ──────────
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = panelRef.current
    if (!el) return

    const stopWheel = (e: WheelEvent) => { e.stopPropagation() }
    const stopMouse = (e: MouseEvent) => { e.stopPropagation() }

    // Capture-Phase: fängt Events ab, bevor Potree sie sieht
    el.addEventListener('wheel', stopWheel, true)
    el.addEventListener('mousedown', stopMouse, true)
    el.addEventListener('mousemove', stopMouse, true)
    el.addEventListener('mouseup', stopMouse, true)
    el.addEventListener('dblclick', stopMouse, true)

    return () => {
      el.removeEventListener('wheel', stopWheel, true)
      el.removeEventListener('mousedown', stopMouse, true)
      el.removeEventListener('mousemove', stopMouse, true)
      el.removeEventListener('mouseup', stopMouse, true)
      el.removeEventListener('dblclick', stopMouse, true)
    }
  }, [])

  // ── Body-Klasse toggeln: verschiebt Potree-Canvas nach rechts ──────────
  // Im Inline-Modus: AppShell managed Layout + Resize, hier nichts tun.
  // Im Legacy-Modus (inline=false): Body-Klasse setzen + Resize dispatchen.
  useEffect(() => {
    if (inline) return // AppShell kümmert sich um Layout + Resize

    if (doc) {
      document.body.classList.add('doc-panel-active')
    }
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 350)

    return () => {
      document.body.classList.remove('doc-panel-active')
      clearTimeout(timer)
      setTimeout(() => window.dispatchEvent(new Event('resize')), 350)
    }
  }, [doc?.id, inline])

  // ── Floating Toolbar — folgt dem Cursor vertikal ──────────────────────
  // Direkte DOM-Manipulation via transform: kein React-State, kein Reflow,
  // läuft vollständig auf dem GPU-Compositor.
  const floatingToolbarRef = useRef<HTMLDivElement>(null)
  const lastMouseY         = useRef(0)
  const scrollRootElRef    = useRef<HTMLDivElement | null>(null)
  const TOOLBAR_THRESHOLD  = 50  // px

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const root = scrollRootElRef.current
      if (!root) return

      // Bounds-Check: nur wenn Cursor über doc-panel-body
      const rect = root.getBoundingClientRect()
      if (
        e.clientX < rect.left || e.clientX > rect.right ||
        e.clientY < rect.top  || e.clientY > rect.bottom
      ) return

      if (Math.abs(e.clientY - lastMouseY.current) < TOOLBAR_THRESHOLD) return
      lastMouseY.current = e.clientY

      const toolbar = floatingToolbarRef.current
      if (!toolbar) return

      // Cursor-Y auf sichtbaren Bereich clampen, Toolbar vertikal zentrieren
      const h = toolbar.offsetHeight
      const y = Math.max(h / 2 + 16, Math.min(window.innerHeight - h / 2 - 16, e.clientY))
      toolbar.style.transform = `translateY(${y}px)`
    }

    // capture: true — läuft VOR panelRef's stopPropagation-Listener
    document.addEventListener('mousemove', onMove, { capture: true, passive: true })
    return () => document.removeEventListener('mousemove', onMove, { capture: true })
  }, [])

  // ── Portal-Slot: header actions in ContentPanel's header ──────────
  const [actionsSlotEl, setActionsSlotEl] = useState<HTMLElement | null>(null)

  // Find the ContentPanel actions slot after mount
  useEffect(() => {
    if (!panelRef.current) return
    const slot = panelRef.current.closest('[data-component="ContentPanel"]')
      ?.querySelector<HTMLElement>('.content-panel__actions-slot')
    setActionsSlotEl(slot ?? null)
  }, [doc?.id])

  if (!doc) return null

  const isReadOnly = mode !== 'edit'
  const isOpen = !!doc

  return (
  <>
    <div
      ref={panelRef}
      data-component="DocumentEditor"
      data-document-id={doc.id}
      data-mode={mode}
      className={`doc-panel ${isOpen ? 'doc-panel-open' : 'doc-panel-closed'}${inline ? ' doc-panel--inline' : ''}`}
    >
      {/* ── Header (title only — actions are portaled into ContentPanel) ── */}
      <div className="doc-panel-header">
        <div className="doc-panel-header-text">
          <input
            className="doc-panel-title"
            data-element="input-doc-title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            onBlur={handleTitleBlur}
            readOnly={isReadOnly}
            placeholder="Dokumenttitel …"
          />
          {!isReadOnly && <SaveIndicator />}
          <span className="doc-panel-subtitle">
            {isReadOnly ? 'Lese-Modus' : 'Bearbeitung'} — {new Date(doc.updatedAt).toLocaleDateString('de-DE')}
          </span>
        </div>
      </div>

      {/* ── Header Actions → portaled into ContentPanel header ── */}
      {actionsSlotEl && createPortal(
        <div className="doc-panel-header-actions">
          {/* Scroll-Nav Toggle */}
          <button
            className={`doc-panel-toggle-btn ${isScrollNavEnabled ? 'is-active' : ''}`}
            data-element="btn-scroll-nav-toggle"
            onClick={() => setIsScrollNavEnabled(v => !v)}
            title={isScrollNavEnabled ? 'Scroll-Navigation deaktivieren' : 'Scroll-Navigation aktivieren'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1.5a6 6 0 0 0-6 6c0 4.2 6 9.5 6 9.5s6-5.3 6-9.5a6 6 0 0 0-6-6z"/>
              <rect x="9.5" y="4.5" width="5" height="5" rx="0.8"/>
              <line x1="12" y1="17" x2="12" y2="19.5"/>
              <line x1="2.5" y1="21.5" x2="21.5" y2="21.5"/>
              <circle cx="2.5"  cy="21.5" r="1.5"/>
              <circle cx="12"   cy="21.5" r="1.5"/>
              <circle cx="21.5" cy="21.5" r="1.5"/>
            </svg>
          </button>

          {/* Export */}
          <button
            data-element="btn-export-doc"
            onClick={() => setShowHeaderExport(v => !v)}
            title="Report exportieren"
            className="doc-panel-header-btn"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span>Export</span>
          </button>

          {showHeaderExport && (
            <div className="doc-panel-header-export-dropdown">
              <button
                className="doc-panel-header-export-dropdown__item"
                onClick={() => { handleExport('md'); setShowHeaderExport(false) }}
              >
                Markdown (.md)
              </button>
              <button
                className="doc-panel-header-export-dropdown__item"
                onClick={() => { handleExport('pdf'); setShowHeaderExport(false) }}
              >
                PDF (.pdf)
              </button>
            </div>
          )}

          {/* Edit / View toggle */}
          {isReadOnly ? (
            <button
              data-element="btn-edit-doc"
              onClick={() => useAnnotationStore.getState().openDocumentEditor(doc.id)}
              title="Bearbeiten"
              className="doc-panel-header-btn"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
              </svg>
              <span>Bearbeiten</span>
            </button>
          ) : (
            <button
              data-element="btn-view-doc"
              onClick={() => openDocumentViewer(doc.id)}
              title="Lese-Modus"
              className="doc-panel-header-btn"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
              </svg>
              <span>Lesen</span>
            </button>
          )}
        </div>,
        actionsSlotEl
      )}

      {/* ── Scroll-Nav-Hinweis ──────────────────────────────────────── */}
      {isReadOnly && isScrollNavEnabled && (
        <div className="doc-panel-scroll-hint" data-section="scroll-nav-hint">
          <span>Scroll-Navigation aktiv</span>
        </div>
      )}

      {/* ── Editor Content (scrollable body) ──────────────────────────── */}
      <div
        ref={scrollRootRef}
        className="doc-panel-body"
        data-section="doc-editor-content"
        data-scroll-root
        style={{ position: 'relative' }}
      >
        <EditorContent editor={editor} />
        {/* Slash-Command-Popup (React statt ProseMirror-Decorations) */}
        {editor && <SlashCommandPopup editor={editor} commands={slashCommands} />}
      </div>

    </div>

    {/* ── Floating Toolbar — schwebt rechts neben dem ContentPanel, folgt Cursor Y ──── */}
    {!isReadOnly && editor && createPortal(
      <div
        ref={floatingToolbarRef}
        className="doc-panel-floating-toolbar"
        style={{ transform: 'translateY(50vh)' }}
      >
        <EditorToolbar
          editor={editor}
          currentCloudId={currentCloudId ?? undefined}
          onInsertViewSnapshot={handleInsertViewSnapshot}
          onExport={handleExport}
        />
      </div>,
      document.body
    )}

    {/* ── Snapshot Trail: rechter Bildschirmrand im Lese-Modus ──── */}
    {isReadOnly && (
      <SnapshotTrail
        editor={editor}
        scrollRoot={scrollRootEl}
        onSnapshotClick={(snapshotJson) => {
          setSelectedAnnotation(null)
          const viewer = viewerRef.current
          if (!viewer) return
          try {
            const snap: ViewSnapshot = JSON.parse(snapshotJson)
            applyViewSnapshot(viewer, snap, flyToCamera)
          } catch { /* ungültiger JSON */ }
        }}
      />
    )}
  </>
  )
}

// ── Slash-Command Popup (React-basiert) ─────────────────────────────────────

function SlashCommandPopup({ editor, commands }: { editor: any; commands: SlashCommandItem[] }) {
  const [slash, setSlash] = useState<{ query: string; from: number } | null>(null)
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null)

  useEffect(() => {
    function onUpdate() {
      if (!editor?.state) { setSlash(null); return }
      const q = getSlashQuery(editor.state)
      setSlash(q)

      if (q && editor.view) {
        try {
          const coords = editor.view.coordsAtPos(editor.state.selection.from)
          const editorRect = editor.view.dom.closest('.doc-panel-body')?.getBoundingClientRect()
          if (editorRect) {
            setPopupPos({
              top: coords.bottom - editorRect.top + 4,
              left: coords.left - editorRect.left,
            })
          }
        } catch { /* ignore */ }
      }
    }

    editor.on('selectionUpdate', onUpdate)
    editor.on('update', onUpdate)
    return () => {
      editor.off('selectionUpdate', onUpdate)
      editor.off('update', onUpdate)
    }
  }, [editor])

  if (!slash || !popupPos) return null

  const filtered = commands.filter(cmd =>
    cmd.aliases.some(a => a.startsWith(slash.query)) ||
    cmd.label.toLowerCase().startsWith(slash.query)
  )
  if (filtered.length === 0) return null

  function executeCommand(cmd: SlashCommandItem) {
    const currentSlash = getSlashQuery(editor.state)
    if (currentSlash) {
      const { tr } = editor.state
      tr.delete(currentSlash.from, editor.state.selection.from)
      editor.view.dispatch(tr)
    }
    cmd.action()
  }

  return (
    <div
      className="slash-cmd-popup"
      style={{ position: 'absolute', top: popupPos.top, left: popupPos.left }}
    >
      {filtered.map((cmd, i) => (
        <div
          key={cmd.label}
          className={`slash-cmd-item${i === 0 ? ' slash-cmd-item--active' : ''}`}
          onMouseDown={e => { e.preventDefault(); executeCommand(cmd) }}
        >
          <span className="slash-cmd-icon">{cmd.icon}</span>
          <span className="slash-cmd-text">
            <span className="slash-cmd-label">{cmd.label}</span>
            <span className="slash-cmd-desc">{cmd.description}</span>
          </span>
        </div>
      ))}
    </div>
  )
}
